// DEPRECATED: regenerate via TS codegen — yuklar-xm0.5
/**
 * Auth API functions
 */

import { apiClient, ApiError } from "./client";
import { saveAccount } from "../auth-storage";
import { getFileUrl } from "./files";
import type { UnwrapData } from "./types";
import type { components } from "./types.generated";
import { setLanguage as saveLanguage } from "../i18n";

type Schemas = components["schemas"];

// Auth DTOs re-exported from generated OpenAPI schemas (yuklar-xm0.5.2).
// Source of truth: api/src/router/auth + api/src/router/users Path A.
export type AccessToken = Schemas["AccessToken"];
export type UserPreferences = Schemas["UserPreferences"];
export type PhotoField = Schemas["PhotoField"];
export type AuthUser = Schemas["AuthUser"];
/**
 * Runtime shape of `/auth/sign-in`, `/auth/verify`, `/auth/sign-in-firebase`,
 * and `/access-tokens/refresh-token`: `AuthUser` with the freshly minted
 * `access_token` attached by `generateAccessToken`. The bare `AuthUser` schema
 * intentionally omits `access_token`; only the signed-in shape carries it.
 */
export type SignedInUser = Schemas["SignedInUser"];
export type SignInRequest = Schemas["SignInRequest"];
export type SignUpRequest = Schemas["SignUpRequest"];
export type VerifyEmailRequest = Schemas["VerifyEmailRequest"];
// `SignUpResponse` is enveloped in the spec ({status, data: {successful}});
// apiClient strips the envelope, so consumers see `{ successful: boolean }`.
export type SignUpResponse = UnwrapData<Schemas["SignUpResponse"]>;

/**
 * Check whether an email is registered.
 */
export const checkEmail = async (email: string): Promise<boolean> => {
  return await apiClient.get<boolean>(
    `/auth/check-email/${encodeURIComponent(email.trim())}`,
  );
};

const TOKEN_KEY = "yuklar_access_token";
const REFRESH_TOKEN_KEY = "yuklar_refresh_token";
const USER_KEY = "yuklar_user";

/** localStorage key holding the signed-in user (single source of truth). */
export const AUTH_USER_STORAGE_KEY = USER_KEY;

/** Extract the user id from a raw `yuklar_user` localStorage value (null-safe). */
export function userIdFromStorageValue(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as SignedInUser).id ?? null;
  } catch {
    return null;
  }
}

/** Remove all auth tokens from localStorage. */
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

const persistAuthSession = (
  user: SignedInUser,
  options?: { applyLanguage?: boolean },
) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(TOKEN_KEY, user.access_token.id);
    localStorage.setItem(REFRESH_TOKEN_KEY, user.access_token.refresh_token_id);
    localStorage.setItem(USER_KEY, JSON.stringify(user));

    if (options?.applyLanguage && user.preferences?.language) {
      saveLanguage(user.preferences.language, { skipServer: true });
    }
  }
};

/**
 * Sign in with email and password
 */
export const signIn = async (data: SignInRequest): Promise<SignedInUser> => {
  const user = await apiClient.post<SignedInUser>("/auth/sign-in", {
    email: data.email,
    password: data.password,
    device_id: data.device_id || getDeviceId(),
  });

  persistAuthSession(user, { applyLanguage: true });

  // Save account for fast sign-in
  saveAccount({
    id: user.id,
    email: user.email,
    displayName: user.name,
    avatarUrl: user.photo?.photo_id
      ? getFileUrl(user.photo.photo_id)
      : undefined,
  });

  return user;
};

/**
 * Sign up - sends verification code to email
 * Returns success status, not user data (verification required)
 */
export const signUp = async (data: SignUpRequest): Promise<SignUpResponse> => {
  return await apiClient.post<SignUpResponse>("/auth/sign-up", {
    name: data.name,
    email: data.email,
    password: data.password,
    phone_number: data.phone_number,
    birth_date: data.birth_date,
    device_id: data.device_id || getDeviceId(),
  });
};

/**
 * Verify email with code - completes registration
 */
export const verifyEmail = async (
  data: VerifyEmailRequest,
): Promise<SignedInUser> => {
  const user = await apiClient.post<SignedInUser>("/auth/verify", {
    email: data.email,
    verification_code: data.verification_code,
  });

  persistAuthSession(user, { applyLanguage: true });

  // Save account for fast sign-in
  saveAccount({
    id: user.id,
    email: user.email,
    displayName: user.name,
    avatarUrl: user.photo?.photo_id
      ? getFileUrl(user.photo.photo_id)
      : undefined,
  });

  return user;
};

/**
 * Sign out - clear local storage
 */
export const signOut = async (): Promise<void> => {
  try {
    await apiClient.post("/auth/logout");
  } catch {
    // Ignore logout errors
  }

  if (typeof window !== "undefined") {
    clearTokens();
  }
};

/**
 * Sign out of every device - revokes all sessions server-side, then clears
 * local storage. Unlike `signOut`, a failed revocation must NOT look like
 * success (this is a compromise-remediation action): on error we keep the
 * local session and rethrow so callers can surface the failure and retry.
 */
export const logoutAllDevices = async (): Promise<void> => {
  await apiClient.post("/auth/logout-all");

  if (typeof window !== "undefined") {
    clearTokens();
  }
};

/**
 * Get current user from localStorage
 */
export const getCurrentUser = (): SignedInUser | null => {
  if (typeof window === "undefined") return null;

  try {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;
    return JSON.parse(userJson) as SignedInUser;
  } catch {
    return null;
  }
};

/**
 * Get stored access token
 */
export const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored refresh token
 */
export const getRefreshToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Generate or get device ID for this browser
 */
const getDeviceId = (): string => {
  if (typeof window === "undefined") return "server";

  const DEVICE_ID_KEY = "yuklar_device_id";
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = `web_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
};

export type SignInFirebaseRequest = Schemas["SignInFirebaseRequest"];

/**
 * Get the redirect path after authentication
 * If user has an entity, go to the appropriate dashboard based on entity type
 * If user doesn't have an entity, check for pending applications
 * - If there are applications in review, go to application status page
 * - If no active applications, go to application form
 */
export const getPostAuthRedirect = (user: AuthUser): string => {
  if (user.is_admin) {
    return "/admin/dashboard";
  }

  if (user.entity && user.user_of_entity_id) {
    return "/dashboard";
  }

  // No entity — send to /apply. The app layout's UserEntityProvider
  // will fetch application status and redirect to /application-status
  // if there's a pending application.
  return "/apply";
};

/**
 * Sign in with Firebase (Google/Apple)
 * Sends the Firebase ID token to backend for verification
 */
export const signInWithFirebase = async (
  data: SignInFirebaseRequest,
): Promise<SignedInUser> => {
  const user = await apiClient.post<SignedInUser>("/auth/sign-in-firebase", {
    id_token: data.id_token,
    email: data.email,
    device_id: data.device_id || getDeviceId(),
  });

  persistAuthSession(user, { applyLanguage: true });

  // Save account for fast sign-in
  saveAccount({
    id: user.id,
    email: user.email,
    displayName: user.name,
    avatarUrl: user.photo?.photo_id
      ? getFileUrl(user.photo.photo_id)
      : undefined,
  });

  return user;
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = async (): Promise<SignedInUser> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new ApiError("No refresh token available", 401);
  }

  const user = await apiClient.post<SignedInUser>(
    "/access-tokens/refresh-token",
    {
      refresh_token_id: refreshToken,
      device_id: getDeviceId(),
    },
  );

  persistAuthSession(user);

  return user;
};

export { ApiError };
